// Coach Resources Service
// Handles TSSAA and DCCSL information, rules, regulations, and contact details

// Resource Categories
export const RESOURCE_CATEGORIES = {
  TSSAA: 'tssaa',
  DCCSL: 'dccsl',
  RULES: 'rules',
  FORMS: 'forms',
  CONTACTS: 'contacts',
  TRAINING: 'training',
  ELIGIBILITY: 'eligibility',
  COMPLIANCE: 'compliance'
};

// Resource Types
export const RESOURCE_TYPES = {
  DOCUMENT: 'document',
  LINK: 'link',
  CONTACT: 'contact',
  FORM: 'form',
  VIDEO: 'video',
  GUIDE: 'guide'
};

// Mock TSSAA Resources
const mockTSSAAResources = [
  {
    id: 'tssaa-handbook',
    title: 'TSSAA Official Handbook',
    category: RESOURCE_CATEGORIES.TSSAA,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'Complete handbook with rules, regulations, and procedures for Tennessee high school athletics',
    url: 'https://tssaa.org/handbook',
    lastUpdated: '2024-01-15',
    priority: 'high',
    required: true,
    tags: ['rules', 'regulations', 'official']
  },
  {
    id: 'tssaa-eligibility',
    title: 'Student Eligibility Requirements',
    category: RESOURCE_CATEGORIES.ELIGIBILITY,
    type: RESOURCE_TYPES.GUIDE,
    description: 'Guidelines for student-athlete eligibility including academic and residency requirements',
    url: 'https://tssaa.org/eligibility',
    lastUpdated: '2024-01-10',
    priority: 'high',
    required: true,
    tags: ['eligibility', 'academic', 'requirements']
  },
  {
    id: 'tssaa-forms',
    title: 'Required Forms and Documents',
    category: RESOURCE_CATEGORIES.FORMS,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'Collection of required forms for student-athlete participation',
    url: 'https://tssaa.org/forms',
    lastUpdated: '2024-01-12',
    priority: 'medium',
    required: true,
    tags: ['forms', 'documents', 'participation']
  },
  {
    id: 'tssaa-coaching-requirements',
    title: 'Coaching Certification Requirements',
    category: RESOURCE_CATEGORIES.TRAINING,
    type: RESOURCE_TYPES.GUIDE,
    description: 'Requirements for coaching certification and continuing education',
    url: 'https://tssaa.org/coaching',
    lastUpdated: '2024-01-08',
    priority: 'high',
    required: true,
    tags: ['coaching', 'certification', 'training']
  },
  {
    id: 'tssaa-calendar',
    title: 'TSSAA Athletic Calendar',
    category: RESOURCE_CATEGORIES.TSSAA,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'Official calendar with important dates, deadlines, and tournament schedules',
    url: 'https://tssaa.org/calendar',
    lastUpdated: '2024-01-20',
    priority: 'medium',
    required: false,
    tags: ['calendar', 'dates', 'tournaments']
  }
];

// Mock DCCSL Resources
const mockDCCSLResources = [
  {
    id: 'dccsl-constitution',
    title: 'DCCSL Constitution and Bylaws',
    category: RESOURCE_CATEGORIES.DCCSL,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'Davidson County Christian School League governing documents',
    url: 'https://dccsl.org/constitution',
    lastUpdated: '2024-01-05',
    priority: 'high',
    required: true,
    tags: ['constitution', 'bylaws', 'governance']
  },
  {
    id: 'dccsl-schedule',
    title: 'DCCSL Game Schedules',
    category: RESOURCE_CATEGORIES.DCCSL,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'Current season schedules for all DCCSL sports',
    url: 'https://dccsl.org/schedules',
    lastUpdated: '2024-01-18',
    priority: 'high',
    required: false,
    tags: ['schedule', 'games', 'season']
  },
  {
    id: 'dccsl-rules',
    title: 'DCCSL Playing Rules',
    category: RESOURCE_CATEGORIES.RULES,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'Sport-specific playing rules and modifications for DCCSL competition',
    url: 'https://dccsl.org/rules',
    lastUpdated: '2024-01-14',
    priority: 'high',
    required: true,
    tags: ['rules', 'playing', 'competition']
  },
  {
    id: 'dccsl-officials',
    title: 'Officials Information',
    category: RESOURCE_CATEGORIES.DCCSL,
    type: RESOURCE_TYPES.CONTACT,
    description: 'Contact information for game officials and scheduling',
    url: 'https://dccsl.org/officials',
    lastUpdated: '2024-01-16',
    priority: 'medium',
    required: false,
    tags: ['officials', 'contact', 'scheduling']
  }
];

// Mock Sport-Specific Rules
const mockSportRules = [
  {
    id: 'football-rules',
    sport: 'Football',
    title: 'High School Football Rules',
    category: RESOURCE_CATEGORIES.RULES,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'NFHS football rules and TSSAA modifications',
    url: 'https://nfhs.org/football-rules',
    lastUpdated: '2024-01-10',
    priority: 'high',
    keyChanges: [
      'Updated targeting rule enforcement',
      'New helmet contact penalties',
      'Modified overtime procedures'
    ]
  },
  {
    id: 'basketball-rules',
    sport: 'Basketball',
    title: 'High School Basketball Rules',
    category: RESOURCE_CATEGORIES.RULES,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'NFHS basketball rules and interpretations',
    url: 'https://nfhs.org/basketball-rules',
    lastUpdated: '2024-01-12',
    priority: 'high',
    keyChanges: [
      'Shot clock implementation guidelines',
      'Technical foul procedures',
      'Uniform regulations updates'
    ]
  },
  {
    id: 'baseball-rules',
    sport: 'Baseball',
    title: 'High School Baseball Rules',
    category: RESOURCE_CATEGORIES.RULES,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'NFHS baseball rules and safety requirements',
    url: 'https://nfhs.org/baseball-rules',
    lastUpdated: '2024-01-08',
    priority: 'high',
    keyChanges: [
      'Bat certification requirements',
      'Pitching limitations',
      'Weather delay procedures'
    ]
  },
  {
    id: 'softball-rules',
    sport: 'Softball',
    title: 'High School Softball Rules',
    category: RESOURCE_CATEGORIES.RULES,
    type: RESOURCE_TYPES.DOCUMENT,
    description: 'NFHS softball rules and equipment standards',
    url: 'https://nfhs.org/softball-rules',
    lastUpdated: '2024-01-09',
    priority: 'high',
    keyChanges: [
      'Equipment certification updates',
      'Pitching circle regulations',
      'Game timing modifications'
    ]
  }
];

// Mock Contact Information
const mockContacts = [
  {
    id: 'tssaa-office',
    organization: 'TSSAA',
    title: 'TSSAA Main Office',
    category: RESOURCE_CATEGORIES.CONTACTS,
    contact: {
      name: 'Tennessee Secondary School Athletic Association',
      address: '3333 Lebanon Pike, Hermitage, TN 37076',
      phone: '(615) 889-6740',
      fax: '(615) 889-5175',
      email: 'info@tssaa.org',
      website: 'https://tssaa.org'
    },
    hours: 'Monday-Friday: 8:00 AM - 4:30 PM',
    services: ['Eligibility questions', 'Rule interpretations', 'Forms and documents']
  },
  {
    id: 'dccsl-commissioner',
    organization: 'DCCSL',
    title: 'DCCSL Commissioner',
    category: RESOURCE_CATEGORIES.CONTACTS,
    contact: {
      name: 'John Smith',
      title: 'League Commissioner',
      phone: '(615) 555-0150',
      email: 'commissioner@dccsl.org',
      website: 'https://dccsl.org'
    },
    services: ['League scheduling', 'Rule clarifications', 'Dispute resolution']
  },
  {
    id: 'athletic-director',
    organization: 'East End Prep',
    title: 'Athletic Director',
    category: RESOURCE_CATEGORIES.CONTACTS,
    contact: {
      name: 'Mike Johnson',
      title: 'Athletic Director',
      phone: '(615) 555-0100',
      email: 'mjohnson@eastendprep.edu',
      office: 'Athletic Office, Main Building'
    },
    hours: 'Monday-Friday: 7:30 AM - 4:00 PM',
    services: ['Coaching support', 'Equipment needs', 'Scheduling assistance']
  },
  {
    id: 'officials-coordinator',
    organization: 'DCCSL',
    title: 'Officials Coordinator',
    category: RESOURCE_CATEGORIES.CONTACTS,
    contact: {
      name: 'Sarah Davis',
      title: 'Officials Coordinator',
      phone: '(615) 555-0175',
      email: 'officials@dccsl.org'
    },
    services: ['Game officials', 'Official assignments', 'Training information']
  }
];

// Mock Training Resources
const mockTrainingResources = [
  {
    id: 'nfhs-courses',
    title: 'NFHS Learning Center Courses',
    category: RESOURCE_CATEGORIES.TRAINING,
    type: RESOURCE_TYPES.LINK,
    description: 'Required and recommended courses for coaches',
    url: 'https://nfhslearn.com',
    priority: 'high',
    required: true,
    courses: [
      {
        name: 'Fundamentals of Coaching',
        required: true,
        duration: '3 hours',
        description: 'Basic coaching principles and responsibilities'
      },
      {
        name: 'Concussion in Sports',
        required: true,
        duration: '2 hours',
        description: 'Recognition and management of concussions'
      },
      {
        name: 'Heat Illness Prevention',
        required: true,
        duration: '1 hour',
        description: 'Prevention and treatment of heat-related illnesses'
      },
      {
        name: 'Sudden Cardiac Arrest',
        required: true,
        duration: '1 hour',
        description: 'Recognition and response to cardiac emergencies'
      },
      {
        name: 'Protecting Students from Abuse',
        required: true,
        duration: '2 hours',
        description: 'Creating safe environments for student-athletes'
      }
    ]
  },
  {
    id: 'first-aid-cpr',
    title: 'First Aid/CPR Certification',
    category: RESOURCE_CATEGORIES.TRAINING,
    type: RESOURCE_TYPES.GUIDE,
    description: 'Required certification for all head coaches',
    provider: 'American Red Cross or American Heart Association',
    renewalPeriod: '2 years',
    priority: 'high',
    required: true
  }
];

// Coach Resources Service Class
class CoachResourcesService {
  static baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  // Get all resources
  static async getAllResources(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.sport) queryParams.append('sport', filters.sport);
      if (filters.required !== undefined) queryParams.append('required', filters.required);
      if (filters.search) queryParams.append('search', filters.search);
      
      const response = await fetch(`${this.baseURL}/coach-resources?${queryParams}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.resources || [];
    } catch (error) {
      console.error('Error fetching resources:', error);
      throw error;
    }
  }
  
  // Get TSSAA resources
  static async getTSSAAResources() {
    return this.getAllResources({ category: RESOURCE_CATEGORIES.TSSAA });
  }
  
  // Get DCCSL resources
  static async getDCCSLResources() {
    return this.getAllResources({ category: RESOURCE_CATEGORIES.DCCSL });
  }
  
  // Get sport-specific rules
  static async getSportRules(sport = null) {
    const filters = { category: RESOURCE_CATEGORIES.RULES };
    if (sport) {
      filters.sport = sport;
    }
    return this.getAllResources(filters);
  }
  
  // Get contact information
  static async getContacts(organization = null) {
    try {
      const queryParams = new URLSearchParams();
      if (organization) queryParams.append('organization', organization);
      
      const response = await fetch(`${this.baseURL}/coach-resources/contacts?${queryParams}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.contacts || [];
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw error;
    }
  }
  
  // Get training resources
  static async getTrainingResources() {
    return this.getAllResources({ category: RESOURCE_CATEGORIES.TRAINING });
  }
  
  // Get required resources
  static async getRequiredResources() {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const allResources = [
      ...mockTSSAAResources,
      ...mockDCCSLResources,
      ...mockTrainingResources
    ];
    
    return allResources.filter(resource => resource.required);
  }
  
  // Search resources
  static async searchResources(query) {
    try {
      const [resources, contacts] = await Promise.all([
        this.getAllResources({ search: query }),
        this.getContacts()
      ]);
      
      // Filter contacts locally for search term
      const searchTerm = query.toLowerCase();
      const matchingContacts = contacts.filter(contact =>
        contact.title?.toLowerCase().includes(searchTerm) ||
        contact.organization?.toLowerCase().includes(searchTerm) ||
        contact.contactName?.toLowerCase().includes(searchTerm)
      );
      
      return {
        resources,
        contacts: matchingContacts
      };
    } catch (error) {
      console.error('Error searching resources:', error);
      throw error;
    }
  }
  
  // Get resource by ID
  static async getResource(id) {
    try {
      const response = await fetch(`${this.baseURL}/coach-resources/${id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching resource:', error);
      throw error;
    }
  }
  
  // Get resource statistics
  static async getResourceStatistics() {
    try {
      const response = await fetch(`${this.baseURL}/coach-resources/statistics`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }
  
  // Create new resource
  static async createResource(resourceData, file = null) {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Token exists:', !!token);
      console.log('Sending resource data:', resourceData);
      console.log('File provided:', !!file);
      
      let body;
      let headers = {
        'Authorization': `Bearer ${token}`
      };
      
      if (file) {
        // Use FormData for file uploads
        const formData = new FormData();
        
        // Append all resource data fields
        Object.keys(resourceData).forEach(key => {
          if (resourceData[key] !== null && resourceData[key] !== undefined) {
            if (Array.isArray(resourceData[key])) {
              formData.append(key, JSON.stringify(resourceData[key]));
            } else {
              formData.append(key, resourceData[key]);
            }
          }
        });
        
        // Append the file
        formData.append('document', file);
        body = formData;
        // Don't set Content-Type header for FormData - browser will set it with boundary
      } else {
        // Use JSON for regular data
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(resourceData);
      }
      
      const response = await fetch(`${this.baseURL}/coach-resources`, {
        method: 'POST',
        headers,
        body
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response status:', response.status);
        console.error('Error response text:', errorText);
        
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          console.error('Failed to parse error response as JSON:', e);
        }
        
        console.error('Parsed error data:', errorData);
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map(err => err.msg || err.message || err).join(', ');
          throw new Error(`Validation errors: ${errorMessages}`);
        }
        
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Resource created successfully:', data);
      return data;
    } catch (error) {
      console.error('Error creating resource:', error);
      throw error;
    }
  }
  
  // Update existing resource
  static async updateResource(id, updateData, file = null) {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Updating resource with file:', !!file);
      
      let body;
      let headers = {
        'Authorization': `Bearer ${token}`
      };
      
      if (file) {
        // Use FormData for file uploads
        const formData = new FormData();
        
        // Append all update data fields
        Object.keys(updateData).forEach(key => {
          if (updateData[key] !== null && updateData[key] !== undefined) {
            if (Array.isArray(updateData[key])) {
              formData.append(key, JSON.stringify(updateData[key]));
            } else {
              formData.append(key, updateData[key]);
            }
          }
        });
        
        // Append the file
        formData.append('document', file);
        body = formData;
        // Don't set Content-Type header for FormData - browser will set it with boundary
      } else {
        // Use JSON for regular data
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(updateData);
      }
      
      const response = await fetch(`${this.baseURL}/coach-resources/${id}`, {
        method: 'PUT',
        headers,
        body
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating resource:', error);
      throw error;
    }
  }
  
  // Create new contact
  static async createContact(contactData) {
    try {
      const response = await fetch(`${this.baseURL}/coach-resources/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(contactData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  }
  
  // Update existing contact
  static async updateContact(id, updateData) {
    try {
      const response = await fetch(`${this.baseURL}/coach-resources/contacts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  }
  
  // Delete resource
  static async deleteResource(id) {
    try {
      const response = await fetch(`${this.baseURL}/coach-resources/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  }
  
  // Delete contact
  static async deleteContact(id) {
    try {
      const response = await fetch(`${this.baseURL}/coach-resources/contacts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  }

  // Utility functions
  static formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  static getPriorityColor(priority) {
    const colors = {
      'low': 'text-green-600 bg-green-100',
      'medium': 'text-yellow-600 bg-yellow-100',
      'high': 'text-red-600 bg-red-100',
      'critical': 'text-red-800 bg-red-200'
    };
    return colors[priority] || 'text-gray-600 bg-gray-100';
  }
  
  static getTypeIcon(type) {
    const icons = {
      [RESOURCE_TYPES.DOCUMENT]: '📄',
      [RESOURCE_TYPES.LINK]: '🔗',
      [RESOURCE_TYPES.CONTACT]: '📞',
      [RESOURCE_TYPES.FORM]: '📋',
      [RESOURCE_TYPES.VIDEO]: '🎥',
      [RESOURCE_TYPES.GUIDE]: '📖'
    };
    return icons[type] || '📄';
  }
}

export default CoachResourcesService;
export { mockTSSAAResources, mockDCCSLResources, mockSportRules, mockContacts, mockTrainingResources };